import { useEffect, useRef, useState } from 'react'
import uuid from 'react-uuid'
import {
  knownNamesList,
  languagesList,
  replacementsDictionary,
  SpeechToTextStatusesEnum,
  SSMLReservedSymbols
} from '../constants/speech'

import {
  AudioConfig,
  AutoDetectSourceLanguageConfig,
  CancellationReason,
  PhraseListGrammar,
  ResultReason,
  SpeakerAudioDestination,
  SpeechConfig,
  SpeechRecognizer,
  SpeechSynthesizer
} from 'microsoft-cognitiveservices-speech-sdk'

import { getSpeechAuthToken } from '../api'

type UseSpeechArgs = {
  speechEnabled?: boolean,
  isTextToSpeechMuted?: boolean,
  speechToTextStatus?: SpeechToTextStatusesEnum,
  onSpeechStarted?: (startTime: number) => void,
  onSpeechFinished?: () => void,
  onVisemeReceived?: (id: string, offsetInMs: number, visemeId: number, animation?: string) => void
}

export type SpeechToTextArgs = {
  onInit: () => void,
  onRecognizing: (resultText?: string) => void,
  onRecognized: () => void
}

type TextToSpeechArgs = {
  text?: string,
  voice: string,
  speed?: number,
  language?: string,
  ssmlTemplate?: string
}

export const useSpeech = ({
  speechEnabled = true,
  isTextToSpeechMuted = true,
  speechToTextStatus = SpeechToTextStatusesEnum.Inactive,
  onSpeechStarted = (startTime: number) => {},
  onSpeechFinished = () => {},
  onVisemeReceived = () => {}
}: UseSpeechArgs) => {

  const textToSpeechSpeaker = useRef<SpeakerAudioDestination>()
  const synthesizerRef = useRef<SpeechSynthesizer>()
  const isPlayingRef = useRef(false);

  const terminateTextToSpeech = () => {
    if (textToSpeechSpeaker.current) {
      textToSpeechSpeaker.current.pause();
      textToSpeechSpeaker.current = undefined;
    }
    if (isPlayingRef.current) {
      isPlayingRef.current = false;
      defer(() => onSpeechFinished?.());
    }
  };

  useEffect(() => {
    if (isTextToSpeechMuted) {
      terminateTextToSpeech();
    }
  }, [isTextToSpeechMuted, isPlayingRef.current]);

  const [speechToTextStatusValue, setSpeechToTextStatus] = useState(speechToTextStatus)
  const recognizer = useRef<SpeechRecognizer>()

  const defer = (fn: () => void) => {
    if (typeof queueMicrotask === 'function') queueMicrotask(fn);
    else setTimeout(fn, 0);
  };

  const textToSpeech = (messageArgs: TextToSpeechArgs) => {
    const preparedMessage = buildSSMLMessage(messageArgs)

    if (isTextToSpeechMuted) {
      console.error('Speech disabled in environment settings.')
      return
    }

    if (!preparedMessage) {
      console.error('No message to pronounce.')
      return
    }

    getSpeechAuthToken()
      .then((auth) => {
        const speaker = new SpeakerAudioDestination()
        textToSpeechSpeaker.current = speaker

        speaker.onAudioStart = () => {
          console.log('AudioStart');
          isPlayingRef.current = true;
          defer(() => onSpeechStarted?.(Date.now()));
        }

        const speechConfig = SpeechConfig.fromAuthorizationToken(auth.access_token, auth.region)
        const audioConfig = AudioConfig.fromSpeakerOutput(speaker)
        const synthesizer  = new SpeechSynthesizer(speechConfig, audioConfig)
        synthesizerRef.current = synthesizer

        speaker.onAudioEnd = () => {
          console.log('AudioEnd');
          if (isPlayingRef.current) defer(() => onSpeechFinished?.());
          isPlayingRef.current = false;
        }

        synthesizer.visemeReceived = (_s, e) => {
          if (textToSpeechSpeaker.current) {
            const offsetInMs = e.audioOffset / 10000;
            defer(() => onVisemeReceived?.(uuid(), offsetInMs, e.visemeId, e.animation));
          }
        }

        synthesizer.speakSsmlAsync(
          preparedMessage,
          (_result) => {
            // console.log("Synthesizer finished")
            synthesizer?.close()
          },
          (_error) => {
            // console.log("Synthesizer failed")
            synthesizer?.close()
            terminateTextToSpeech()
          }
        )
      })
      .catch(() => {
        console.error('Text-to-speech failed. Auth token cannot be retrieved.')
        onSpeechFinished()
    })
  }

  const buildSSMLMessage = ({ text, voice, speed, language, ssmlTemplate }: TextToSpeechArgs) => {
    const formattedText = formatSSMLMessageText(text)
    if (!formattedText) return

    const pickedSpeed = speed || 1.0
    const pickedLanguage = language || languagesList[0]

    const pickedSSMLTemplate = ssmlTemplate ||
      `<speak version="1.0" 
         xmlns="http://www.w3.org/2001/10/synthesis"
         xmlns:mstts="http://www.w3.org/2001/mstts"
         xml:lang="${pickedLanguage}">
         <voice name="${voice}">"
           <prosody rate="${pickedSpeed}" volume="${isTextToSpeechMuted ? 'silent' : 'default'}">
             %{{text}}
           </prosody>
           <mstts:silence type="Tailing" value="0ms"/>
         </voice>
       </speak>`

    return pickedSSMLTemplate.replace('%{{text}}', formattedText)
  }

  const formatSSMLMessageText = (text?: string) => {
    if (!text) return;
    let result = text;
  
    result = result.replaceAll(/\s?\[doc\d*\]/g, ''); 
    result = result.replace(/([*_~#`>])/g, '');
    result = result.replace(/\^\d+\^/g, '');
  
    SSMLReservedSymbols.forEach((pair) => {
      result = result.replaceAll(new RegExp(pair[0], 'gi'), pair[1]);
    });
  
    return result.trim();
  }

  const speechToText = ({ onInit, onRecognizing, onRecognized }: SpeechToTextArgs) => {
    if (!speechEnabled) {
      console.error('Speech disabled in environment settings.')
      return
    }

    terminateTextToSpeech()
    setSpeechToTextStatus(SpeechToTextStatusesEnum.Initializing)

    getSpeechAuthToken()
      .then((auth) => {
        const speechConfig = SpeechConfig.fromAuthorizationToken(auth.access_token, auth.region)
        // speechConfig.setProperty(PropertyId[PropertyId.Conversation_Initial_Silence_Timeout], "0")
        // speechConfig.setProperty(PropertyId[PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs], "0")
        const autoDetectSourceLanguageConfig = AutoDetectSourceLanguageConfig.fromLanguages(languagesList)
        const audioConfig = AudioConfig.fromDefaultMicrophoneInput()

        recognizer.current = SpeechRecognizer.FromConfig(speechConfig, autoDetectSourceLanguageConfig, audioConfig)

        const phrasesConfig = PhraseListGrammar.fromRecognizer(recognizer.current)
        phrasesConfig.addPhrases(knownNamesList)

        recognizer.current.startContinuousRecognitionAsync()
        setSpeechToTextStatus(SpeechToTextStatusesEnum.Active)

        onInit()

        recognizer.current.recognizing = (_s, e) => {
          if (!recognizer.current) return;

          onRecognizing(formatRecognizedText(e.result.text))
        }

        recognizer.current.recognized = (_s, e) => {
          switch (e.result.reason) {
            case ResultReason.RecognizedSpeech:
              if (!recognizer.current) break;

              setTimeout(() => onRecognized())
              terminateSpeechToText()
              break
            case ResultReason.NoMatch:
              break
            default:
              console.error('Unexpected behaviour in #recognized event listener')
              onInit()
              terminateSpeechToText()
              break
          }
        }

        recognizer.current.canceled = (_s, e) => {
          console.log(`CANCELED: Reason=${e.reason}`)

          if (e.reason == CancellationReason.Error) {
            console.log(`"CANCELED: ErrorCode=${e.errorCode}`)
            console.log(`"CANCELED: ErrorDetails=${e.errorDetails}`)
            console.log("CANCELED: Did you set the speech resource key and region values?")
          }

          terminateSpeechToText()
        }
      })
      .catch(() => {
        console.error('Speech-to-text failed. Auth token cannot be retrieved.')
        setSpeechToTextStatus(SpeechToTextStatusesEnum.Inactive)
      })
  }

  const formatRecognizedText = (text: string) => {
    let result = text

    replacementsDictionary.forEach((pair) => {
      result = result.replaceAll(new RegExp(pair[0], 'gi'), pair[1])
    })

    return result.charAt(0).toUpperCase() + result.slice(1)
  }

  const terminateSpeechToText = () => {
    setSpeechToTextStatus(SpeechToTextStatusesEnum.Inactive)

    if (!recognizer.current) return

    recognizer.current?.stopContinuousRecognitionAsync()
    recognizer.current = undefined
  }

  return {
    speechToTextStatus: speechToTextStatusValue,
    textToSpeech,
    terminateTextToSpeech,
    speechToText,
    terminateSpeechToText,
    isSpeechEnabled: speechEnabled
  }
}
